/**
 * Custom Jest snapshot serializer: collapses rendered DOM into a YAML-shaped
 * tree of testID-keyed visible text.
 *
 * Replaces React Native Web's verbose default snapshots with something humans
 * can diff. Strips noisy attributes (class, style, dir, virtualkeyboardpolicy)
 * that change unpredictably across module builds, then walks the tree
 * collecting visible text grouped by `testID` / `data-testid`. Elements marked
 * `data-snapshot-hidden=true` (via `getHideFromSnapshotProp`) are skipped
 * entirely — use that for transient UI like dev-only badges. `isTLabelKey`
 * heuristically detects translation keys so
 * `{testID: 'foo.bar', text: 'foo.bar'}` collapses to a single line rather than
 * duplicating. Output is `key:\n - value` YAML rather than real YAML; the goal
 * is grep-ability, not round-trip parse.
 */

const stripNoisyAttributes = (element: Element | ChildNode): void => {
    if (element.nodeType === 1) {
        const el = element as Element;
        el.removeAttribute('class');
        el.removeAttribute('style');
        el.removeAttribute('dir');
        el.removeAttribute('virtualkeyboardpolicy');
    }
    Array.from(element.childNodes).forEach(stripNoisyAttributes);
};

function getTestId(el: Element): string | null {
    return el.getAttribute('data-testid') || el.getAttribute('testid') || el.getAttribute('testID');
}

type Item = string | Record<string, string>;

function itemsToText(items: Item[]): string {
    return items
        .map((it) => (typeof it === 'string' ? it : `${Object.keys(it)[0]}: ${it[Object.keys(it)[0]]}`))
        .join(' ')
        .trim();
}

export function isTLabelKey(id: string): boolean {
    return id.includes('.') && /^[a-z_][a-z0-9_.]*$/.test(id) && !id.startsWith('.') && !id.endsWith('.');
}

// example: <View {...getHideFromSnapshotProp()} /> cast to any because React
// Native doesnt recognize the prop.
export const getHideFromSnapshotProp = () => ({ dataSet: { snapshotHidden: 'true' } }) as any;

function collectItems(node: Element): Item[] {
    const items: Item[] = [];
    node.childNodes.forEach((child) => {
        if (child.nodeType === 3) {
            const text = child.textContent?.replace(/\s+/g, ' ').trim();
            if (text) items.push(text);
            return;
        }
        if (child.nodeType !== 1) return;
        const el = child as Element;
        if (el.getAttribute('data-snapshot-hidden') === 'true') return;
        const id = getTestId(el);
        const subItems = collectItems(el);
        const value = (el as any).value ?? el.getAttribute('value');
        if (value && subItems.length === 0) {
            subItems.unshift(String(value));
        }
        const text = itemsToText(subItems);
        if (id) {
            if (!text) return;
            if (subItems.length === 1 && typeof subItems[0] === 'string' && (subItems[0] === id || isTLabelKey(id))) {
                items.push(subItems[0]);
            } else {
                items.push({ [id]: text });
            }
        } else {
            items.push(...subItems);
        }
    });
    return items;
}

function dumpYaml(items: Item[]): string {
    let out = '';
    items.forEach((it) => {
        if (typeof it === 'string') {
            out += `${it}\n`;
        } else {
            const key = Object.keys(it)[0];
            out += `${key}:\n  - ${it[key]}\n`;
        }
    });
    return out.trim();
}

// Walk the entire document.body so React portals (e.g. popup.tsx renders
// into document.body via ReactDOM.createPortal) are included in snapshots
// alongside the testing-library render container.
function getSnapshotRoot(val: any): Element {
    const ownerDocument = val.container?.ownerDocument;
    return ownerDocument?.body ?? val.container;
}

module.exports = {
    getHideFromSnapshotProp,
    isTLabelKey,
    test: (val: any): boolean => Boolean(val && val.container),
    print: (val: any): string => {
        const root = getSnapshotRoot(val);
        stripNoisyAttributes(root);
        const items = collectItems(root);
        return dumpYaml(items);
    },
};
